import type { AppearanceChoice } from "@townsy/contracts";
import { avatarPreviewModel } from "./avatarModel";

/**
 * Live 2D preview of the avatar (front view, slim proportions). Every choice in the builder is
 * visible here: body shape, shirt, skin, hair colour + style, outfit. Decorative shapes only; the
 * accessible name is a plain-text description of the whole look.
 */
export function AvatarPreview({ choice, size = 160 }: { choice: AppearanceChoice; size?: number }) {
  const m = avatarPreviewModel(choice);
  const cx = 60;
  const sh = m.shoulderHalf;
  const wa = m.waistHalf;
  const hip = m.hipHalf;
  const torso = `M ${cx - sh} 68 Q ${cx} 62 ${cx + sh} 68 L ${cx + wa} 112 L ${cx + hip} 128 L ${cx - hip} 128 L ${cx - wa} 112 Z`;
  const sleeve = m.jacket ?? m.shirt;
  const longSleeves = choice.outfit === "hoodie" || choice.outfit === "suit";
  const armX = [cx - sh - 7, cx + sh];
  const legX = [cx - 10, cx + 2];
  return (
    <svg
      className="avatar-preview"
      viewBox="0 0 120 230"
      width={size * (120 / 230)}
      height={size}
      role="img"
      aria-label={`Avatar preview: ${m.description}`}
      data-testid="avatar-preview"
      data-body={choice.bodyType}
      data-outfit={choice.outfit}
      data-hair={choice.hairStyle}
    >
      <ellipse cx={cx} cy={216} rx={30} ry={5} fill="rgba(27,31,42,0.12)" />
      {/* behind the body: hood collar and long hair */}
      {m.hood ? <path d={`M ${cx - 19} 72 Q ${cx - 21} 52 ${cx} 50 Q ${cx + 21} 52 ${cx + 19} 72 Z`} fill={m.shirt} stroke="rgba(0,0,0,0.22)" /> : null}
      {m.hairStyle === "long" ? <path d={`M ${cx - 18} 40 Q ${cx - 19} 20 ${cx} 20 Q ${cx + 19} 20 ${cx + 18} 40 L ${cx + 21} 86 Q ${cx} 80 ${cx - 21} 86 Z`} fill={m.hair} /> : null}

      {/* legs */}
      {legX.map((x) => (
        <rect key={`leg${x}`} x={x} y={124} width={8} height={86} rx={3.5} fill={m.legs} />
      ))}
      {choice.outfit === "sport" ? <rect x={cx - hip} y={122} width={hip * 2} height={26} rx={4} fill="#2B2F3C" /> : null}
      {legX.map((x) => (
        <ellipse key={`shoe${x}`} cx={x + 4} cy={210} rx={7} ry={3.6} fill={m.shoes} stroke="rgba(0,0,0,0.25)" strokeWidth={0.6} />
      ))}

      {/* arms */}
      {armX.map((x) => (
        <g key={`arm${x}`}>
          <rect x={x} y={70} width={7} height={52} rx={3.5} fill={m.skin} />
          <rect x={x} y={70} width={7} height={longSleeves ? 48 : 20} rx={3.5} fill={sleeve} />
          <circle cx={x + 3.5} cy={124} r={4} fill={m.skin} />
        </g>
      ))}

      {/* torso (shirt), dress skirt, suit jacket */}
      <path d={torso} fill={m.jacket ?? m.shirt} />
      {m.jacket ? (
        <>
          <path d={`M ${cx - 7} 66 L ${cx} 96 L ${cx + 7} 66 Z`} fill={m.shirt} />
          <path d={`M ${cx - 2} 70 L ${cx + 2} 70 L ${cx + 3} 90 L ${cx} 94 L ${cx - 3} 90 Z`} fill="#8B1E2B" />
        </>
      ) : null}
      {m.skirt ? <path d={`M ${cx - wa} 110 L ${cx + wa} 110 L ${cx + hip + 9} 160 L ${cx - hip - 9} 160 Z`} fill={m.skirt} /> : null}
      {m.hood ? (
        <>
          <rect x={cx - 10} y={98} width={20} height={14} rx={4} fill="rgba(0,0,0,0.14)" />
          <line x1={cx - 4} y1={70} x2={cx - 4} y2={84} stroke="rgba(255,255,255,0.8)" strokeWidth={1.2} />
          <line x1={cx + 4} y1={70} x2={cx + 4} y2={84} stroke="rgba(255,255,255,0.8)" strokeWidth={1.2} />
        </>
      ) : null}

      {/* neck + head */}
      <rect x={cx - 4} y={54} width={8} height={12} rx={2} fill={m.skin} />
      <circle cx={cx} cy={40} r={17} fill={m.skin} />
      <circle cx={cx - 6} cy={42} r={1.8} fill="#1B1F2A" />
      <circle cx={cx + 6} cy={42} r={1.8} fill="#1B1F2A" />
      <path d={`M ${cx - 5} 49 Q ${cx} 53 ${cx + 5} 49`} stroke="#1B1F2A" strokeWidth={1.4} fill="none" strokeLinecap="round" />

      {/* hair (front) */}
      {m.hairStyle === "short" || m.hairStyle === "long" || m.hairStyle === "bun" ? (
        <path d={`M ${cx - 17} 40 Q ${cx - 18} 20 ${cx} 20 Q ${cx + 18} 20 ${cx + 17} 40 Q ${cx + 12} 29 ${cx} 29 Q ${cx - 12} 29 ${cx - 17} 40 Z`} fill={m.hair} />
      ) : null}
      {m.hairStyle === "bun" ? <circle cx={cx} cy={17} r={7.5} fill={m.hair} /> : null}
      {m.hairStyle === "curly"
        ? [
            [cx - 15, 33],
            [cx - 10, 25],
            [cx - 3, 21],
            [cx + 4, 21],
            [cx + 11, 25],
            [cx + 15, 33],
            [cx - 17, 41],
            [cx + 17, 41],
          ].map(([x, y]) => <circle key={`curl${x}-${y}`} cx={x} cy={y} r={6.5} fill={m.hair} />)
        : null}
      {m.cap ? (
        <>
          <path d={`M ${cx - 16} 38 Q ${cx - 12} 34 ${cx - 8} 37 L ${cx - 16} 44 Z`} fill={m.hair} />
          <path d={`M ${cx + 16} 38 Q ${cx + 12} 34 ${cx + 8} 37 L ${cx + 16} 44 Z`} fill={m.hair} />
          <path d={`M ${cx - 18} 36 Q ${cx - 18} 18 ${cx} 18 Q ${cx + 18} 18 ${cx + 18} 36 Z`} fill={m.cap} stroke="rgba(0,0,0,0.2)" />
          <ellipse cx={cx} cy={36} rx={21} ry={3.6} fill={m.cap} stroke="rgba(0,0,0,0.25)" />
        </>
      ) : null}
    </svg>
  );
}
